import { Component, Renderer2, signal, input, inject, OnDestroy, computed, effect } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FilterSelection } from 'src/app/components/filters/filters.component';
import { MappedFilter } from 'src/app/products/services/filters-adapter.service';
import { FilterManagement } from '../../class/filter-management';
import { GlobalFiltersService } from 'src/app/components/filters/services/global-filters.service';
import { CategoryLayoutData } from 'src/app/core/interfaces/store-configuration.interface';
import { createDefaultCategoryLayoutData } from 'src/app/core/utilities/type-defaults.util';
import { MenuCollapseService } from 'src/app/components/dynamic-header/services/menu-collapse.service';

@Component({
  standalone: true,
  selector: 'app-category-filter-layout-2',
  templateUrl: './category-filter-layout-2.component.html',
  styleUrls: ['./category-filter-layout-2.component.scss'],
  imports: [CommonModule]
})
export class CategoryFilterLayout2Component extends FilterManagement implements OnDestroy {
  override globalFiltersService: GlobalFiltersService;
  private renderer = inject(Renderer2);
  private menuCollapseService = inject(MenuCollapseService);

  // Private signals
  private _isHamburgerMenu = signal<boolean>(false);
  private _selectedFilter = signal<number>(-1);
  private _currentAppliedFilters = signal<FilterSelection[] | null>([]);
  private _categoryLayoutData = signal<CategoryLayoutData>(createDefaultCategoryLayoutData());
  private _selectedDeviceType = signal<string>('');
  private _category = signal<string>('');
  private _availableFilterAttributes = signal<MappedFilter[] | null>([]);
  private _firstRender = signal<boolean>(true);
  private _filterData = signal<{ filterIcon: string }>({ filterIcon: '' });
  private _downArrowIndex = signal<number[]>([]);
  private _upArrowIndex = signal<number[]>([0]);
  private _setTimeout = signal<ReturnType<typeof setTimeout>>(setTimeout(() => { }, 0));

  // Public readonly signals for template
  readonly isHamburgerMenuSignal = this._isHamburgerMenu.asReadonly();
  readonly selectedFilterSignal = this._selectedFilter.asReadonly();
  readonly currentAppliedFiltersSignal = this._currentAppliedFilters.asReadonly();
  readonly categoryLayoutDataSignal = this._categoryLayoutData.asReadonly();
  readonly selectedDeviceTypeSignal = this._selectedDeviceType.asReadonly();
  readonly categorySignal = this._category.asReadonly();
  readonly availableFilterAttributesSignal = this._availableFilterAttributes.asReadonly();
  readonly firstRenderSignal = this._firstRender.asReadonly();
  readonly filterDataSignal = this._filterData.asReadonly();
  readonly downArrowIndexSignal = this._downArrowIndex.asReadonly();
  readonly upArrowIndexSignal = this._upArrowIndex.asReadonly();

  // Computed signals
  readonly hasCurrentAppliedFilters = computed(() =>
    (this._currentAppliedFilters()?.length ?? 0) > 0
  );

  readonly hasAvailableFilterAttributes = computed(() =>
    (this._availableFilterAttributes()?.length ?? 0) > 0
  );

  readonly hasCategory = computed(() =>
    !!this._category()
  );

  currentAppliedFilters = input<FilterSelection[] | null>([]);
  categoryLayoutData = input<CategoryLayoutData>(createDefaultCategoryLayoutData());
  selectedDeviceType = input<string>('');
  category = input<string>('');
  availableFilterAttributes = input<MappedFilter[] | null>([]);

  // Effects to handle input changes
  private currentAppliedFiltersEffect = effect(() => {
    const value = this.currentAppliedFilters();
    this._currentAppliedFilters.set(value);
  });

  private categoryLayoutDataEffect = effect(() => {
    const value = this.categoryLayoutData();
    this._categoryLayoutData.set(value);
  });

  private selectedDeviceTypeEffect = effect(() => {
    const value = this.selectedDeviceType();
    this._selectedDeviceType.set(value);
  });

  private categoryEffect = effect(() => {
    const value = this.category();
    if (value) {
      this._firstRender.set(true);
      this._downArrowIndex.set([]);
      this._upArrowIndex.set([0]);
      this._category.set(value);
    }
  });

  private availableFilterAttributesEffect = effect(() => {
    const value = this.availableFilterAttributes();
    if (value?.length && this._firstRender()) {
      const downArrowIndex: number[] = [];
      value.forEach((availableAttribute, i) => {
        if (i > 0) downArrowIndex.push(i);
      });
      this._downArrowIndex.set(downArrowIndex);
      this._firstRender.set(false);
    }
    this._availableFilterAttributes.set(value);
  });

  constructor() {
    const globalFiltersService = inject(GlobalFiltersService);
    super(globalFiltersService);
    this.globalFiltersService = globalFiltersService;
  }

  toggleHamburgerMenu() {
    const currentValue = this._isHamburgerMenu();
    this._isHamburgerMenu.set(!currentValue);
    this.menuCollapseService.setHamburgerMenu(true);
  }

  selectFilter(filterIndex: number) {
    const currentSelected = this._selectedFilter();
    if (currentSelected === filterIndex) {
      this._selectedFilter.set(-1);
    } else {
      this._selectedFilter.set(filterIndex);
    }
  }

  openFilterDropdown(index: number) {
    const currentUpArrowIndex = this._upArrowIndex();
    const currentDownArrowIndex = this._downArrowIndex();

    if (!currentUpArrowIndex.includes(index)) {
      this._upArrowIndex.set([...currentUpArrowIndex, index]);
    }
    this._downArrowIndex.set(currentDownArrowIndex.filter(i => i !== index));
  }

  closeFilterDropdown(index: number) {
    const currentUpArrowIndex = this._upArrowIndex();
    const currentDownArrowIndex = this._downArrowIndex();

    if (!currentDownArrowIndex.includes(index)) {
      this._downArrowIndex.set([...currentDownArrowIndex, index]);
    }
    this._upArrowIndex.set(currentUpArrowIndex.filter(i => i !== index));
  }

  handleMenuMouseOut(event: MouseEvent): void {
    const targetElement = event.target as HTMLElement;
    this.renderer.removeStyle(targetElement, 'color');
    this.renderer.removeStyle(targetElement, 'background-color');

    const timeoutId = setTimeout(() => {
      const menuItemToActivate = document.getElementsByClassName('hover-color-active');
      const parentElementMenuItemToActivate = menuItemToActivate[0]?.parentElement;

      if (menuItemToActivate && menuItemToActivate.length > 0) {
        for (let i = 0; i < menuItemToActivate.length; i++) {
          this.renderer.setStyle(
            menuItemToActivate[i],
            'background-color',
            this._categoryLayoutData().filterData?.styleConfiguration?.desktopStyleConfiguration?.checkBoxBackgroundColor
          );
        }
      }

      const menuItemToDeActivate = document.getElementsByClassName('hover-color-inactive');
      if (menuItemToDeActivate.length > 0) {
        for (let i = 0; i < menuItemToDeActivate.length; i++) {
          this.renderer.removeStyle(menuItemToDeActivate[i], 'color');
          this.renderer.removeStyle(menuItemToDeActivate[i], 'background-color');
        }
      }
    }, 10);

    this._setTimeout.set(timeoutId);
  }

  ngOnDestroy(): void {
    clearTimeout(this._setTimeout());
  }
}
